// Path: frontend/src/lib/api/core.ts
import axios from 'axios';

// ==========================================
// 📚 Type Definitions (Core)
// ==========================================

export interface SlideDetail {
  id: number;
  slide_index: number;
  html_code: string;
  side_guide_content: string;
  side_guide: string;
  audit: string;
  is_final: boolean;
  version_number: string;
  plan_id: number;
  updated_at: string;
  memory?: string;
}

// 单页教研记忆快照（AI 在页边教案结尾生成的结构化 JSON）
export interface MemorySnapshot {
  page: string;
  segment: string;
  page_mode: string;
  theme_mode: string;
  tool_state: string;
  main_goal: string;
  teacher_signal: string;
  student_first_action: string;
  microcheck: string;
  handoff_hook: string;
  media_decision: string;
}

export interface SlideVersionInfo {
  id: number;
  version_number: string;
}

export interface ChatMessage {
  id: number;
  role: 'user' | 'assistant';
  content: string;
  created_at: string;
  input_tokens: number;
  output_tokens: number;
  input_money?: number;
  output_money?: number;
  total_money?: number;  // 累计总金额
  sys_prompt_id?: number;
}

export interface ChatResponse {
  ai_response: string;
  chat_history: ChatMessage[];
}

export interface OSSUploadResponse {
  file_url: string;
  file_name: string;
  file_size?: number;
}

export interface CloudResource {
  id: number;
  user_id: number;
  lesson_id: number;
  artifact_id: number;
  file_name: string;
  file_type: string;
  public_url: string;
  file_size: number;
  created_at: string;
}

export interface SlideCloudResources {
  slide_id: number;
  cloud_resources: CloudResource[];
}

export interface OSSDeleteResponse {
  status: string;
  message: string;
  resource_id: number;
}

export interface SysPrompt {
  id: number;
  name: string;
  category: string;
  updated_at: string;
  prompt_content?: string;
}

export interface InlinePrompt {
  id: number;
  sys_prompt_id: number;
  step: string;
  updated_at: string;
  content?: string;
}

export interface School {
  id: number;
  schoolname: string;
  is_deleted: boolean;
}

export interface SchoolTime {
  id: number;
  school_id: number;
  day_of_week: number;   // 1-7 (1=周一, 7=周日)
  start_time: string;    // HH:MM:SS
  end_time: string;      // HH:MM:SS
  is_deleted: boolean;
}

export interface SchoolTimeCreate {
  day_of_week: number;
  start_time: string;
  end_time: string;
}

export interface SchoolTimeUpdate {
  day_of_week?: number;
  start_time?: string;
  end_time?: string;
}

export interface User {
  id: number;
  username: string;
  role: 'sys' | 'admin' | 'teacher' | 'student';
  school_id?: number;
  school?: School;
  avatar_url?: string;
  memo?: string;
  has_face_enrolled?: boolean;
  face_login_enabled?: boolean;
  password_login_enabled?: boolean;
}

export type PermissionType = 'view_only' | 'edit_develop';

export interface UserPermissionInfo {
  user_id: number;
  username: string;
  role: string;
  memo?: string;
  permission_type: PermissionType;
}

export interface SchoolPermissionSummary {
  school_id: number;
  schoolname: string;
  user_count: number;
}

export interface SchoolWithUsersPermission extends SchoolPermissionSummary {
  users: UserPermissionInfo[];
}

export interface SchoolPermission {
  id: number;
  school_id: number;
  permission_type: PermissionType;
  user_count?: number;
  school?: School;
}

export interface UserPermission {
  id: number;
  user_id: number;
  permission_type: PermissionType;
  user?: User;
}

export interface AssignedCourse {
  course_id: number;
  course_title: string;
  stages?: Array<{
    stage_id: number;
    stage_title: string;
    sort_order?: number;
    lessons?: Array<{
      lesson_id: number;
      lesson_title: string;
      permission_type: PermissionType;
      sort_order?: number;
    }>;
  }>;
}

export interface PublishSlide {
  id: number;
  slide_index: number;
  version_number: string;
  title: string;
}

export interface PublishPlan {
  id: number;
  version_number: string;
  is_final: boolean;
  slides: PublishSlide[];
}

export interface PublishLesson {
  id: number;
  title: string;
  sort_order: number;
  plans: PublishPlan[];
}

export interface PublishStage {
  id: number;
  title: string;
  sort_order: number;
  lessons: PublishLesson[];
}

export interface PublishCourse {
  id: number;
  title: string;
  description: string;
  stages: PublishStage[];
}

export interface SlideContextResponse {
  slide: { id: number; slide_index: number; version_number: string; is_final: boolean; plan_id: number; };
  plan: { id: number; version_number: string; is_final: boolean; lesson_id: number; };
  lesson: { id: number; title: string; sort_order: number; stage_id: number; };
  stage: { id: number; title: string; sort_order: number; course_id: number; };
}

export interface SlideGuideMessage {
  type: 'connected' | 'start' | 'stop' | 'progress' | 'error' | 'all_done' | 'stopped' | 'chunk';
  content: string;
  data?: any;
}

// ==========================================
// 🏙️ 查找表类型（文档第 15 章）
// ==========================================

export interface City {
  id: number;
  name: string;
  is_deleted?: boolean;
  created_at?: string;
}

export interface District {
  id: number;
  city_id: number;
  name: string;
  is_deleted?: boolean;
  created_at?: string;
}

export interface CourseType {
  id: number;
  code: string;
  name: string;
}

// 机构配置创建返回值（文档 15.6）
export interface SchoolCreateWithConfigResult {
  school: School;
  school_times: SchoolTime[];
  generated_schoolname: string;
}

// 批量导入用户返回值（文档 14.4）
export interface BatchImportResult {
  school_id: number;
  requested_count: number;
  success_count: number;
  failure_count: number;
  successful_users: User[];
  failed_usernames: Array<{ username: string; reason: string }>;
}

// ==========================================
// ⚙️ Core Axios Setup
// ==========================================

export const api = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000/api/v1',
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 180000,
});

export const getToken = (): string | null => {
  const sessionToken = sessionStorage.getItem('token');
  if (sessionToken) return sessionToken;
  
  const localStorageToken = localStorage.getItem('token');
  if (localStorageToken) {
    try {
      const parsedToken = JSON.parse(localStorageToken);
      if (parsedToken.expiry) {
        if (new Date().getTime() > parsedToken.expiry) {
          localStorage.removeItem('token');
          return null;
        }
        return parsedToken.value;
      }
      return localStorageToken;
    } catch {
      return localStorageToken;
    }
  }
  return null;
};

api.interceptors.request.use(config => {
  const token = getToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  
  if (config.url && !config.url.endsWith('/')) {
    const hasQueryParams = config.url.includes('?');
    if (hasQueryParams) {
      const [path, query] = config.url.split('?');
      config.url = `${path}/?${query}`;
    } else {
      config.url = `${config.url}/`;
    }
  }
  
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      const detail = error.response?.data?.detail;
      const code = detail && typeof detail === 'object' ? detail.code : null;
      if (code === 'SESSION_SUPERSEDED') {
        if (!sessionStorage.getItem('session_superseded_shown')) {
          sessionStorage.setItem('session_superseded_shown', '1');
          alert(detail?.message || '该账户已在其他设备登录，当前会话已失效');
        }
      }
      localStorage.removeItem('token');
      sessionStorage.removeItem('token');
      sessionStorage.removeItem('session_superseded_shown');
      window.location.href = '/login';
    }
    console.error('API Request Failed:', error);
    return Promise.reject(error);
  }
);

// ==========================================
// 🚨 Structured API Error Parser
// ==========================================

export interface StructuredApiError {
  code: string | null;
  message: string;
  raw: any;
}

export const parseApiError = (error: any): StructuredApiError => {
  const detail = error?.response?.data?.detail;
  if (detail && typeof detail === 'object') {
    return {
      code: detail.code ?? null,
      message: detail.message ?? JSON.stringify(detail),
      raw: detail,
    };
  }
  if (typeof detail === 'string' && detail.length > 0) {
    return { code: null, message: detail, raw: detail };
  }
  const message = error?.response?.data?.message || error?.message || '请求失败';
  return { code: null, message, raw: error?.response?.data };
};

// ==========================================
// 🤖 AI 接口测试与管理
// ==========================================

export interface GeminiConnectionResponse {
  status: 'success' | 'anomaly' | 'error';
  latency_ms: number;
  message: string;
  current_third: 'gemini' | 'ucloud' | 'unknown';
}

export interface AIModelsSyncResponse {
  status: string;
  message: string;
  updated_count: number;
  current_third: 'gemini' | 'ucloud' | 'unknown';
}

export const testGeminiConnection = async (): Promise<GeminiConnectionResponse> => {
  const response = await api.get<GeminiConnectionResponse>('/ai-test/gemini-connection', { timeout: 300000 });
  return response.data;
};

export const syncAIModels = async (third: string): Promise<AIModelsSyncResponse> => {
  const response = await api.post<AIModelsSyncResponse>('/ai-test/ai-models', { third }, { timeout: 300000 });
  return response.data;
};